import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { Markdown } from "@/components/site/Markdown";
import { ShareBar } from "@/components/site/ShareBar";
import { getChangelogEntry, listChangelogEntries, type ChangelogEntry } from "@/lib/backlink.functions";
import { SITE, formatDate } from "@/lib/site";

const entryQuery = (slug: string) =>
  queryOptions({
    queryKey: ["changelog-entry", slug],
    queryFn: () => getChangelogEntry({ data: { slug } }),
  });

const listQuery = queryOptions({
  queryKey: ["changelog-entries"],
  queryFn: () => listChangelogEntries(),
});

export const Route = createFileRoute("/changelog/$slug")({
  loader: async ({ params, context }) => {
    const entry = await context.queryClient.ensureQueryData(entryQuery(params.slug));
    if (!entry) throw notFound();
    await context.queryClient.ensureQueryData(listQuery);
    return entry;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const e = loaderData as ChangelogEntry;
    const url = `${SITE.url}/changelog/${params.slug}`;
    const title = e.title;
    const desc = e.summary.slice(0, 158);
    return {
      meta: [
        { title: `${title} | ${SITE.name} Changelog` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: e.occurred_at },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: title,
            description: desc,
            datePublished: e.occurred_at,
            dateModified: e.updated_at,
            url,
            author: { "@type": "Organization", name: `${SITE.name} Newsroom`, url: SITE.url },
            publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
            isBasedOn: e.source_url ?? undefined,
          }),
        },
      ],
    };
  },
  component: EntryPage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Not found</p>
        <h1 className="headline-display mt-3 text-4xl">Entry not found</h1>
        <Link to="/changelog" className="mt-6 inline-block underline">Back to changelog</Link>
      </main>
      <SiteFooter />
    </div>
  ),
});

function EntryPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <EntryBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function EntryBody() {
  const params = Route.useParams();
  const { data: entry } = useSuspenseQuery(entryQuery(params.slug));
  const { data: all } = useSuspenseQuery(listQuery);
  if (!entry) return null;
  const e = entry as ChangelogEntry;
  const url = `${SITE.url}/changelog/${params.slug}`;
  const more = (all as ChangelogEntry[]).filter((x) => x.slug !== e.slug).slice(0, 6);

  return (
    <article className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          <li aria-hidden>/</li>
          <li><Link to="/changelog" className="hover:text-accent">Changelog</Link></li>
        </ol>
      </nav>
      <header className="mt-6">
        <p className="eyebrow">Changelog · {e.surface.toUpperCase()} · {e.change_type}</p>
        <h1 className="headline-display mt-3 text-4xl sm:text-5xl">{e.title}</h1>
        <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">
          Occurred {formatDate(e.occurred_at)}
        </p>
      </header>

      <p className="mt-8 text-xl leading-relaxed font-serif text-foreground/90">{e.summary}</p>

      {e.body_md ? (
        <div className="mt-8">
          <Markdown content={e.body_md} />
        </div>
      ) : e.source_url ? (
        <FullReportCTA sourceUrl={e.source_url} sourceTitle={e.source_title} />
      ) : null}

      {e.source_url && (
        <p className="mt-8 text-sm">
          <span className="eyebrow mr-2">Source</span>
          <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="underline text-ochre-deep hover:text-accent">
            {e.source_title ?? e.source_url}
          </a>
        </p>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-b border-border py-5">
        <CiteThis title={e.title} url={url} publishedDate={e.occurred_at} />
        <ShareBar url={url} title={e.title} text={e.summary} />
      </div>

      {more.length > 0 && (
        <section className="mt-16">
          <div className="border-b border-foreground pb-3 mb-6">
            <h2 className="headline-display text-2xl">More from the changelog</h2>
          </div>
          <ul className="space-y-4">
            {more.map((m) => (
              <li key={m.id}>
                <Link to="/changelog/$slug" params={{ slug: m.slug }} className="group block">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{formatDate(m.occurred_at)}</p>
                  <p className="mt-1 font-semibold group-hover:text-accent">{m.title}</p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <NewsletterCTA />
    </article>
  );
}

function FullReportCTA({ sourceUrl, sourceTitle }: { sourceUrl: string; sourceTitle: string | null }) {
  let internalSlug: string | null = null;
  let host = "";
  try {
    const u = new URL(sourceUrl);
    host = u.hostname.replace(/^www\./, "");
    const siteHost = new URL(SITE.url).hostname.replace(/^www\./, "");
    if (host === siteHost) {
      const m = u.pathname.match(/^\/article\/([^/]+)\/?$/);
      if (m) internalSlug = m[1];
    }
  } catch {
    /* ignore */
  }

  return (
    <div className="mt-8 rounded-sm border border-border bg-paper-soft p-6">
      <p className="eyebrow">Full report</p>
      <p className="mt-2 text-base text-foreground/85">
        This changelog entry is a dated, citable summary. Read the full write-up
        {sourceTitle ? ` on ${sourceTitle}` : host ? ` on ${host}` : ""}.
      </p>
      {internalSlug ? (
        <Link
          to="/article/$slug"
          params={{ slug: internalSlug }}
          className="mt-4 inline-block rounded-sm bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-accent"
        >
          Read the full report →
        </Link>
      ) : (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-block rounded-sm bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-accent"
        >
          Read the full report ↗
        </a>
      )}
    </div>
  );
}

